import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  FilePresent as FileIcon,
  Sync as ImportIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

interface FileInfo {
  exists: boolean;
  fileName?: string;
  size?: number;
  modified?: string;
}

interface ImportResult {
  success: boolean;
  message: string;
  deleted?: number;
  inserted?: number;
  byTicker?: Record<string, number>;
}

interface DividendUploadProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

export default function DividendUpload({ open, onClose, onImportSuccess }: DividendUploadProps) {
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch file info on dialog open
  useEffect(() => {
    if (open) {
      fetchFileInfo();
      // Clear previous states
      setUploadSuccess(null);
      setImportResult(null);
      setError(null);
    }
  }, [open]);

  const fetchFileInfo = async () => {
    try {
      const res = await fetch('/api/dividends/file-info');
      const data = await res.json();
      setFileInfo(data);
    } catch (err) {
      console.error('Failed to fetch file info:', err);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/dividends/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setUploadSuccess(`File "${file.name}" uploaded successfully`);
      fetchFileInfo(); // Refresh file info
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

      const res = await fetch('/api/dividends/import', {
        method: 'POST',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Import failed');
      }

      setImportResult(data);
      // Trigger data refresh in parent component
      onImportSuccess?.();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Import timed out. Please check server logs.');
      } else {
        setError(err instanceof Error ? err.message : 'Import failed');
      }
    } finally {
      setImporting(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Don't close on backdrop click or escape key while importing
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={importing}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Dividend Data Manager</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Current File Status */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Dividend File
          </Typography>
          {fileInfo?.exists ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <FileIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {fileInfo.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(fileInfo.size || 0)} | Modified: {formatDate(fileInfo.modified || '')}
                </Typography>
              </Box>
              <Chip label="Available" color="success" size="small" />
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <FileIcon color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No dividend file found
              </Typography>
              <Chip label="Not Found" color="warning" size="small" />
            </Stack>
          )}
        </Paper>

        {/* Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Step 1: Upload Excel File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a new dividend Excel file to replace the existing one. The file should contain
            columns for each REIT ticker with date and dividend amount pairs.
          </Typography>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleUpload}
            ref={fileInputRef}
            style={{ display: 'none' }}
            id="dividend-file-input"
          />
          <label htmlFor="dividend-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
              disabled={uploading}
              fullWidth
            >
              {uploading ? 'Uploading...' : 'Choose Excel File (.xlsx)'}
            </Button>
          </label>

          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<SuccessIcon />}>
              {uploadSuccess}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Import Section */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Step 2: Import to Database
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Parse the uploaded Excel file and import dividend data into MongoDB.
            This will replace all existing dividend records.
          </Typography>

          <Button
            variant="contained"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <ImportIcon />}
            onClick={handleImport}
            disabled={importing || !fileInfo?.exists}
            fullWidth
          >
            {importing ? 'Importing...' : 'Import Dividends to Database'}
          </Button>

          {importResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500}>
                {importResult.message}
              </Typography>
              {importResult.byTicker && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Records by ticker:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                    {Object.entries(importResult.byTicker).map(([ticker, count]) => (
                      <Chip
                        key={ticker}
                        label={`${ticker}: ${count}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Alert>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
